import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import InfinityLoop from "./InfinityLoop";
import { EASE } from "./Reveal";

const LINES = ["Travel", "technology,", "engineered by", "people who", "run travel."];

export default function Hero() {
  const { scrollY } = useScroll();
  const yPanel = useTransform(scrollY, [0, 900], [0, -56]);

  return (
    <section id="top" className="relative overflow-hidden bg-ink">
      <div
        className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full opacity-25 blur-[140px]"
        style={{ background: "radial-gradient(circle, #2B4BEA 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-[1160px] gap-14 px-6 pb-24 pt-[150px] lg:grid-cols-[1.05fr_0.85fr] lg:items-start lg:pt-[164px]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
            className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand-soft"
            data-testid="hero-eyebrow"
          >
            AI &amp; Technology Company · Global
          </motion.p>

          <h1
            className="mt-9 text-[clamp(3.1rem,5.9vw,5.2rem)] font-normal leading-[1.02] tracking-[-0.025em] text-white"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                <motion.span
                  className="block will-change-transform"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, ease: EASE, delay: 0.16 + i * 0.09 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.72 }}
            className="mt-9 max-w-[470px] text-[17px] leading-[1.65] text-[#9a9aa3]"
            data-testid="hero-subline"
          >
            AI-powered SaaS and custom builds for travel companies — at a fraction of
            enterprise cost. Plus end-to-end technology for every other industry.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#travel-tech"
              data-testid="hero-explore-travel-tech-btn"
              className="rounded-[10px] bg-brand px-6 py-3.5 text-[15px] font-medium text-white transition-all duration-300 hover:bg-brand-deep"
            >
              Explore Travel Tech
            </a>
            <a
              href="#travel-tech"
              data-testid="hero-explore-all-solutions-btn"
              className="group inline-flex items-center gap-2 rounded-[10px] border border-white/25 px-6 py-3.5 text-[15px] font-medium text-white transition-colors duration-300 hover:border-white/55"
            >
              Explore All Solutions
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: yPanel }}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.35 }}
          className="relative"
          data-testid="hero-infinity-loop"
        >
          <InfinityLoop className="block aspect-[7/8] w-full lg:aspect-auto lg:h-[680px]" />
        </motion.div>
      </div>
    </section>
  );
}
